import {Box, Paper, Stack, Step, StepConnector, StepLabel, Stepper, styled, Typography} from "@mui/material";
import {ApprovalStatus, ApprovalStepStatus, ZyusetuFragmentFragment} from "../../../graphql/graphql";
import {Check, Clock, FileText, X} from "lucide-react";

interface ZyusetuApprovalFlowSectionProps {
  zyusetu: ZyusetuFragmentFragment;
}

const CustomStepConnector = styled(StepConnector)(({theme}) => ({
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.grey[300],
    borderTopWidth: 3,
    borderRadius: 4,
  },
  '&.Mui-active': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main,
    },
  },
  '&.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const StepIconContainer = styled('div')<{
  completed?: boolean;
  active?: boolean;
  rejected?: boolean;
}>(({theme, completed, active, rejected}) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: completed
    ? theme.palette.primary.main
    : rejected
      ? theme.palette.error.main
      : active
        ? theme.palette.primary.light
        : theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: completed || active || rejected ? theme.palette.common.white : theme.palette.text.secondary,
  transition: 'all 0.2s ease-in-out',
  boxShadow: active
    ? `0 0 0 4px ${theme.palette.primary.light}33`
    : 'none',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const StepWrapper = styled(Box)(({theme}) => ({
  '& .MuiStepLabel-root': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  '& .MuiStepLabel-labelContainer': {
    marginTop: 0,
  },
}));

export function ZyusetuApprovalFlowSection({
                                             zyusetu,
                                           }: ZyusetuApprovalFlowSectionProps) {
  if (!zyusetu.approvalFlow?.steps?.length) {
    return null;
  }
  
  // 作成者のステップを含む配列を作成
  const allSteps = [
    {
      id: 'creator',
      name: '書類作成',
      stepOrder: -1,
      approver: {
        name: zyusetu.user?.name
      },
      stepExecution: [{
        status: zyusetu.approvalStatus === ApprovalStatus.Draft ? ApprovalStepStatus.PendingApproval : ApprovalStepStatus.Approved
      }]
    },
    ...[...zyusetu.approvalFlow.steps ?? []].sort((a, b) => a.stepOrder - b.stepOrder)
  ];
  
  const activeStep = allSteps.findIndex(
    step => step.stepExecution.some(exec => exec.status === "PendingApproval")
  );
  
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
      }}
      elevation={0}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        承認フロー
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        <Stepper
          activeStep={activeStep}
          connector={<CustomStepConnector/>}
          sx={{
            flex: 1,
            '& .MuiStep-root': {
              padding: 0,
            },
          }}
        >
          {allSteps.map((step, index) => {
            const execution = step.stepExecution[0];
            const isCompleted = execution?.status === ApprovalStepStatus.Approved;
            const isPending = execution?.status === ApprovalStepStatus.PendingApproval;
            const isRejected = execution?.status === ApprovalStepStatus.Rejected;
            
            return (
              <Step key={step.id} completed={isCompleted}>
                <StepWrapper>
                  <StepLabel
                    StepIconComponent={({active}) => (
                      <StepIconContainer
                        completed={isCompleted}
                        active={active || isPending}
                        rejected={isRejected}
                      >
                        {step.id === "creator" ? <FileText size={20}/> : isCompleted ? (
                          <Check size={20}/>
                        ) : isRejected ? (
                          <X size={20}/>
                        ) : isPending ? (
                          <Clock size={20}/>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </Typography>
                        )}
                      </StepIconContainer>
                    )}
                  >
                    <Stack spacing={0.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isPending ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {step.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        {step.approver.name}
                      </Typography>
                    </Stack>
                  </StepLabel>
                </StepWrapper>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Paper>
  );
}
